import type { ReactNode } from 'react';
import { Star, StarHalf, Palmtree, Ban, Laptop, PartyPopper, Briefcase } from 'lucide-react';
import type { ChipTone } from '@vnetwork/ui/primitives';

const has = (type: string | null | undefined, needle: string) => (type ?? '').includes(needle);

/**
 * Corner icon for a calendar day, mirroring the legacy work-day type icons.
 * `workNDay` (1 / 0.5 / 0) distinguishes a full vs half vs empty normal day.
 */
export function dayTypeIcon(type: string | null, workNDay: number | null): ReactNode {
  const s = 15;
  if (has(type, 'lễ') || has(type, 'tết')) return <PartyPopper size={s} color="var(--vn-warning-solid)" />;
  if (has(type, 'công tác')) return <Briefcase size={s} color="var(--vn-danger-solid)" />;
  if (has(type, 'remote')) return <Laptop size={s} color="var(--vn-success-solid)" />;
  if (has(type, 'phép năm')) return <Palmtree size={s} color="var(--vn-info-solid)" />;
  if (has(type, 'không phép')) {
    if (workNDay === 0.5) return <StarHalf size={s} color="var(--vn-warning-solid)" />;
    if (workNDay === 0) return <Ban size={s} color="var(--vn-danger-solid)" />;
    return null;
  }
  if (has(type, 'bình thường')) {
    if (workNDay === 1) return <Star size={s} color="var(--vn-warning-solid)" fill="var(--vn-warning-solid)" />;
    if (workNDay === 0.5) return <StarHalf size={s} color="var(--vn-warning-solid)" />;
    return null;
  }
  return null;
}

/** Chip tone for the worked-hours pill — success normal, danger if late, info for remote/holiday/trip. */
export function hoursPillTone(type: string | null, workDayIn: string | null, option: number | null): ChipTone {
  const isNormal = has(type, 'bình thường');
  if (isNormal && workDayIn && (option === 0 || option === null)) {
    const [h, m] = workDayIn.split(':').map(Number);
    const late = h > 8 || (h === 8 && m > 30);
    if (late) return 'danger';
  }
  if (has(type, 'remote') || has(type, 'lễ') || has(type, 'tết') || has(type, 'công tác')) {
    return 'info';
  }
  return 'success';
}

/** Compact hours string: integer as-is, else 2dp; blank for 0/null. */
export function formatWorkHours(time: number | null): string {
  if (time == null || time === 0) return '';
  return Number.isInteger(time) ? String(time) : time.toFixed(2);
}
